import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {TaggedItemType, TagsDashboardFetchParamsType} from 'app/tags/types';
import {TAGGEDITEMS_API_URL, TAGS_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {ListResponseType, TagSerializedType} from 'sweet-types';

export const TAGS_RTK_TAGS = {
  TAGS: 'Tags',
} as const;

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [TAGS_RTK_TAGS.TAGS],
  })

  .injectEndpoints({
    endpoints: (build) => ({
      tags: build.query<
        ListResponseType<TagSerializedType>,
        TagsDashboardFetchParamsType
      >({
        query: (params) => ({
          url: TAGS_API_URL,
          params: params ?? {},
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: () => gt('Could not fetch tags.'),
              byStatus: {},
              silenceByStatus: {404: true},
            },
          })(args, api);
        },
        providesTags: providesTagsByURL(TAGS_RTK_TAGS.TAGS),
      }),
      updateTag: build.mutation<
        TagSerializedType,
        Pick<TagSerializedType, 'id' | 'url' | 'name'>
      >({
        query: (tag) => ({
          url: tag.url,
          method: 'PATCH',
          body: tag,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not update tag.'),
            onSuccess: () => gt('Tag renamed'),
          })(args, api);
        },
        invalidatesTags: () => [{type: TAGS_RTK_TAGS.TAGS, id: 'list'}],
      }),
      deleteTag: build.mutation<unknown, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not delete tag.'),
            onSuccess: () => gt('Tag deleted'),
          })(args, api);
        },
        invalidatesTags: () => [{type: TAGS_RTK_TAGS.TAGS, id: 'list'}],
      }),
      removeTag: build.mutation<unknown, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        invalidatesTags: () => [{type: TAGS_RTK_TAGS.TAGS, id: 'list'}],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Could not remove tag.'),
            onSuccess: () => gt('Tag removed.'),
          })(args, api);
        },
      }),
      addTags: build.mutation<unknown, Array<TaggedItemType>>({
        query: (tags) => ({
          url: TAGGEDITEMS_API_URL,
          method: 'POST',
          body: tags,
        }),
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Could not add tag.'),
            onSuccess: () => gt('Tag added.'),
          })(args, api);
        },
        invalidatesTags: () => [{type: TAGS_RTK_TAGS.TAGS, id: 'list'}],
      }),
    }),
  });

export const {
  useTagsQuery: useTags,
  useDeleteTagMutation: useDeleteTag,
  useUpdateTagMutation: useUpdateTag,
  useRemoveTagMutation: useRemoveTag,
  useAddTagsMutation: useAddTags,
} = extendedAPI;
